/**
 * The mechanics under `RecordTable`.
 *
 * Four pages hand-rolled a record list on the raw `ui/table` primitive not
 * because they wanted different rows, but because `RecordTable` could not sort,
 * filter, select, or paginate and each of them needed at least one of those. A
 * page with no way to opt in forks, and a fork carries a second, subtly
 * different copy of every mechanic it needed plus every mechanic it did not.
 *
 * So this file owns behaviour and owns no part of what a row contains. Every
 * descriptor it accepts carries a function — `cell`, `sortValue`, `value` —
 * never a type name, because a type name is the first token of a rendering
 * vocabulary the layer would then have to keep extending. The rule that keeps
 * this honest: **if the layer names a domain field, it has taken ownership it
 * should not have**, and the only names below are mechanical ones such as
 * `id`, `direction`, and `selectedRowIds`.
 *
 * Every capability defaults to off. A page that opts into none gets the row
 * order it passed in, unpaged and unfiltered.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type Table,
  type VisibilityState,
} from "@tanstack/react-table";

import type { ReactNode } from "react";

/** The identity the engine gives the column that opens the record. */
export const PRIMARY_COLUMN_ID = "__primary";

export interface RecordColumn<T> {
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /**
   * Aligns header and cell together. `right` for counts, grades, and totals;
   * `center` for a short symmetric value such as a credit count or a mark.
   */
  align?: "right" | "center";
  /** Cell-only typography or a truncation bound on a free-text column. */
  cellClassName?: string;
  /**
   * Stable identity for sort and visibility state. Defaults to the column's
   * position, which is fine until a page reorders its columns — supply one
   * before persisting either piece of state anywhere.
   */
  id?: string;
  /**
   * The comparable this column sorts on. Supplying it is what makes the column
   * sortable; there is no separate flag, because a sortable column with nothing
   * to compare is not a state the layer should be able to represent.
   */
  sortValue?: (row: T) => string | number | null | undefined;
  /** Hidden until the reader turns it on. Requires `columnVisibility`. */
  defaultHidden?: boolean;
  /**
   * The column's own name in the visibility menu and the sort announcement.
   * Only needed where `header` is not already a plain string.
   */
  name?: string;
}

/**
 * The column that opens the record. Separate from `columns` because it is the
 * only one that carries the focusable control, and the component owns that
 * control so keyboard reachability cannot drift page to page.
 */
export interface RecordPrimaryColumn<T> {
  header: ReactNode;
  label: (row: T) => ReactNode;
  /** The muted line under the label: identifiers, program, term. */
  detail?: (row: T) => ReactNode;
  /**
   * Overrides the accessible name where the label alone does not identify the
   * record — two import runs of one source file share a name and differ only by
   * mode and start time.
   */
  ariaLabel?: (row: T) => string;
  /** As `RecordColumn.sortValue`. Usually the label, folded for comparison. */
  sortValue?: (row: T) => string | number | null | undefined;
  name?: string;
}

export interface RecordFacetOption {
  value: string;
  /** Defaults to the value itself. */
  label?: ReactNode;
  /**
   * Overrides the count the engine derives from the rows. A page whose server
   * does the filtering must supply this, because its rows are already narrowed
   * and would report every unselected bucket as empty.
   */
  count?: number;
}

/**
 * One filter dimension. `value` is a function rather than a field name for the
 * same reason `cell` is: the moment the layer accepts `"stage"` it owes every
 * consumer a way to spell every other field, and it has become a schema.
 */
export interface RecordFacet<T> {
  id: string;
  label: string;
  /** The bucket this row belongs to. `null` keeps the row out of every bucket. */
  value: (row: T) => string | null;
  /**
   * Buckets to offer, in this order. Omit and the engine derives them from the
   * rows, ordered by first appearance so a page's own row order survives.
   */
  options?: RecordFacetOption[];
  /**
   * One bucket at a time: picking a second replaces the first rather than
   * widening the filter. This is not a styling choice — a facet backed by an
   * endpoint that takes `?status=` can express exactly one value, and a
   * multi-select control over it would silently drop everything after the
   * first.
   */
  single?: boolean;
}

export type RecordSortDirection = "asc" | "desc";
export interface RecordSort {
  columnId: string;
  direction: RecordSortDirection;
}

/** Selected values per facet id. An absent or empty entry means "all". */
export type RecordFacetSelection = Record<string, string[]>;

/**
 * Which concerns the server owns. The engine still owns the control and the
 * state in manual mode — it just stops applying them to the rows, because the
 * rows it was handed already reflect them. This is the seam that lets a list
 * move server-side as a backend change behind an unchanged component contract.
 */
export interface RecordTableManualModes {
  sort?: boolean;
  facets?: boolean;
  pagination?: boolean;
}

export interface RecordTableOptions<T> {
  rows: T[];
  rowId: (row: T) => string;
  primary: RecordPrimaryColumn<T>;
  columns: RecordColumn<T>[];
  /**
   * Whole-row emphasis at either width — a closed term reading as history
   * rather than as something still being decided. A function over the row, so
   * the layer applies it without knowing what makes a row quiet.
   */
  rowClassName?: (row: T) => string | undefined;

  /** Renders sort affordances in the header. Columns opt in with `sortValue`. */
  sortable?: boolean;
  sort?: RecordSort | null;
  defaultSort?: RecordSort | null;
  onSortChange?: (sort: RecordSort | null) => void;

  facets?: RecordFacet<T>[];
  facetSelection?: RecordFacetSelection;
  defaultFacetSelection?: RecordFacetSelection;
  onFacetSelectionChange?: (selection: RecordFacetSelection) => void;

  /** Offers the reader a per-column show/hide menu. */
  columnVisibility?: boolean;
  hiddenColumns?: string[];
  onHiddenColumnsChange?: (columnIds: string[]) => void;

  selectable?: boolean;
  selectedRowIds?: string[];
  onSelectedRowIdsChange?: (rowIds: string[]) => void;

  /** Rows per page. Absent or zero means every row on one page. */
  pageSize?: number;
  pageIndex?: number;
  onPageIndexChange?: (pageIndex: number) => void;
  /** Total rows behind a manually paginated list. */
  rowCount?: number;

  manual?: RecordTableManualModes;
}

export interface RecordVisibleColumn<T> {
  id: string;
  column: RecordColumn<T>;
  sortable: boolean;
  direction: RecordSortDirection | null;
}

export interface RecordFacetBucket<T> {
  facet: RecordFacet<T>;
  /** `count` is absent where no trustworthy one exists; see `facetBuckets`. */
  options: RecordFacetOption[];
  selected: string[];
}

export interface RecordTableEngine<T> {
  /** Escape hatch for anything this interface has not needed to name yet. */
  table: Table<T>;
  /** The rows to render, after sort, facets, and paging. */
  rows: T[];
  rowId: (row: T) => string;
  primary: RecordPrimaryColumn<T>;
  rowClassName?: (row: T) => string | undefined;

  sortable: boolean;
  primarySortable: boolean;
  primaryDirection: RecordSortDirection | null;
  visibleColumns: RecordVisibleColumn<T>[];
  toggleSort: (columnId: string) => void;

  facetBuckets: RecordFacetBucket<T>[];
  toggleFacetValue: (facetId: string, value: string) => void;
  clearFacets: () => void;
  activeFacetCount: number;

  columnVisibility: boolean;
  hideableColumns: { id: string; name: string; hidden: boolean }[];
  toggleColumnVisible: (columnId: string) => void;

  selectable: boolean;
  selectedRowIds: string[];
  isRowSelected: (rowId: string) => boolean;
  toggleRowSelected: (rowId: string) => void;
  /** All rows on the current page, or none of them. */
  togglePageSelected: () => void;
  pageSelectionState: "none" | "some" | "all";
  clearSelection: () => void;

  paginated: boolean;
  pageIndex: number;
  pageCount: number;
  pageSize: number;
  /** Rows behind the whole list, not just this page. */
  totalRowCount: number;
  /** 1-based inclusive bounds of the current page, for "x-y of z". */
  pageBounds: { first: number; last: number };
  goToPage: (pageIndex: number) => void;
}

/** `header` is usually the name already; `name` covers the cases where it is not. */
function columnName(header: ReactNode, name: string | undefined, fallback: string): string {
  if (name) return name;
  return typeof header === "string" ? header : fallback;
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export function useRecordTable<T>(options: RecordTableOptions<T>): RecordTableEngine<T> {
  const {
    rows,
    rowId,
    primary,
    columns,
    sortable = false,
    facets,
    columnVisibility = false,
    selectable = false,
    pageSize = 0,
    rowCount,
    manual,
  } = options;

  const paginated = pageSize > 0;
  const facetList = useMemo(() => facets ?? [], [facets]);

  // Column ids are computed once per column list and reused by sort state,
  // visibility state, and the rendered header, so the three cannot disagree.
  const columnIds = useMemo(
    () => columns.map((column, index) => column.id ?? String(index)),
    [columns],
  );

  const columnDefs = useMemo<ColumnDef<T>[]>(() => {
    const defs: ColumnDef<T>[] = [
      {
        id: PRIMARY_COLUMN_ID,
        // `null` sorts as absent rather than as the empty string, so a record
        // with nothing in its primary column sinks instead of leading.
        accessorFn: (row) => primary.sortValue?.(row) ?? undefined,
        enableSorting: !!primary.sortValue,
        enableHiding: false,
        sortUndefined: "last",
      },
    ];

    columns.forEach((column, index) => {
      defs.push({
        id: columnIds[index],
        accessorFn: (row) => column.sortValue?.(row) ?? undefined,
        enableSorting: !!column.sortValue,
        enableHiding: true,
        sortUndefined: "last",
      });
    });

    // A facet is a hidden column carrying the bucket key. It never renders; it
    // exists so filtering, and the counts that go with it, stay in the engine
    // rather than being re-derived by every page that wants a filter chip.
    facetList.forEach((facet) => {
      defs.push({
        id: facetColumnId(facet.id),
        accessorFn: (row) => facet.value(row) ?? undefined,
        enableSorting: false,
        enableHiding: false,
        filterFn: (row, columnId, filterValue) => {
          const selected = filterValue as string[];
          if (!selected || selected.length === 0) return true;
          const value = row.getValue(columnId);
          return typeof value === "string" && selected.includes(value);
        },
      });
    });

    return defs;
  }, [columnIds, columns, facetList, primary]);

  const [internalSorting, setInternalSorting] = useState<SortingState>(() =>
    toSortingState(options.defaultSort ?? null),
  );
  const [internalSelection, setInternalSelection] = useState<RecordFacetSelection>(
    () => options.defaultFacetSelection ?? {},
  );
  // `flatMap` over the whole list, not `filter().map()`: the fallback id is the
  // column's position in `columns`, and filtering first renumbers from the
  // hidden columns alone — so a page that marks its second column
  // `defaultHidden` without giving it an id hides the first one instead.
  const [internalHidden, setInternalHidden] = useState<string[]>(() =>
    columns.flatMap((column, index) => (column.defaultHidden ? [column.id ?? String(index)] : [])),
  );
  const [internalRowSelection, setInternalRowSelection] = useState<string[]>([]);
  const [internalPageIndex, setInternalPageIndex] = useState(0);

  const sorting = options.sort !== undefined
    ? toSortingState(options.sort)
    : internalSorting;
  const facetSelection = options.facetSelection ?? internalSelection;
  const hiddenColumns = options.hiddenColumns ?? internalHidden;
  const selectedRowIds = options.selectedRowIds ?? internalRowSelection;
  const pageIndex = options.pageIndex ?? internalPageIndex;

  const columnFilters = useMemo<ColumnFiltersState>(
    () => facetList
      .map((facet) => ({ id: facetColumnId(facet.id), value: facetSelection[facet.id] ?? [] }))
      .filter((filter) => (filter.value as string[]).length > 0),
    [facetList, facetSelection],
  );

  const visibilityState = useMemo<VisibilityState>(() => {
    const state: VisibilityState = {};
    for (const id of hiddenColumns) state[id] = false;
    return state;
  }, [hiddenColumns]);

  const rowSelectionState = useMemo<RowSelectionState>(() => {
    const state: RowSelectionState = {};
    for (const id of selectedRowIds) state[id] = true;
    return state;
  }, [selectedRowIds]);

  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex, pageSize: paginated ? pageSize : Number.MAX_SAFE_INTEGER }),
    [pageIndex, pageSize, paginated],
  );

  const table = useReactTable<T>({
    data: rows,
    columns: columnDefs,
    getRowId: (row) => rowId(row),
    state: {
      sorting,
      columnFilters,
      columnVisibility: visibilityState,
      rowSelection: rowSelectionState,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualSorting: !!manual?.sort,
    manualFiltering: !!manual?.facets,
    manualPagination: !!manual?.pagination,
    rowCount: manual?.pagination ? rowCount : undefined,
    enableRowSelection: selectable,
    enableSorting: sortable,
    enableHiding: columnVisibility,
    autoResetPageIndex: false,
    // Donor controls operate on this exact instance. Route its controlled
    // mutations through the same native callbacks, including manual modes.
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      setSorting(first ? { columnId: first.id, direction: first.desc ? "desc" : "asc" } : null);
    },
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === "function" ? updater(columnFilters) : updater;
      const selection: RecordFacetSelection = {};
      for (const facet of facetList) {
        const value = next.find((filter) => filter.id === facetColumnId(facet.id))?.value;
        const values = Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
        selection[facet.id] = facet.single ? values.slice(-1) : values;
      }
      setFacetSelection(selection);
    },
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === "function" ? updater(visibilityState) : updater;
      const hidden = columnIds.filter((id) => next[id] === false);
      if (options.hiddenColumns === undefined) setInternalHidden(hidden);
      options.onHiddenColumnsChange?.(hidden);
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater(pagination) : updater;
      goToPage(next.pageIndex);
    },
  });

  const setSorting = useCallback((next: RecordSort | null) => {
    if (options.sort === undefined) setInternalSorting(toSortingState(next));
    options.onSortChange?.(next);
  }, [options]);

  const toggleSort = useCallback((columnId: string) => {
    const current = sorting[0];
    if (!current || current.id !== columnId) {
      setSorting({ columnId, direction: "asc" });
      return;
    }
    // asc -> desc -> unsorted. The third press has to reach the page's own row
    // order, which is a real state and the only one that shows the list as the
    // server returned it.
    setSorting(current.desc ? null : { columnId, direction: "desc" });
  }, [setSorting, sorting]);

  const setFacetSelection = useCallback((next: RecordFacetSelection) => {
    if (options.facetSelection === undefined) setInternalSelection(next);
    // A narrowed list starts on page one. Reset before the facet command:
    // controlled URL owners clear page in that command too, and React Router
    // does not queue same-tick search-param writes. A later pager callback
    // would overwrite the newly selected facet with its previous URL closure.
    if (options.pageIndex === undefined) setInternalPageIndex(0);
    options.onPageIndexChange?.(0);
    options.onFacetSelectionChange?.(next);
  }, [options]);

  const toggleFacetValue = useCallback((facetId: string, value: string) => {
    const current = facetSelection[facetId] ?? [];
    const isSingle = facetList.find((facet) => facet.id === facetId)?.single ?? false;
    const next = current.includes(value)
      ? current.filter((entry) => entry !== value)
      : isSingle ? [value] : [...current, value];
    setFacetSelection({ ...facetSelection, [facetId]: next });
  }, [facetList, facetSelection, setFacetSelection]);

  const clearFacets = useCallback(() => setFacetSelection({}), [setFacetSelection]);

  const toggleColumnVisible = useCallback((columnId: string) => {
    const next = hiddenColumns.includes(columnId)
      ? hiddenColumns.filter((id) => id !== columnId)
      : [...hiddenColumns, columnId];
    if (options.hiddenColumns === undefined) setInternalHidden(next);
    options.onHiddenColumnsChange?.(next);
  }, [hiddenColumns, options]);

  const setSelectedRowIds = useCallback((next: string[]) => {
    if (options.selectedRowIds === undefined) setInternalRowSelection(next);
    options.onSelectedRowIdsChange?.(next);
  }, [options]);

  const toggleRowSelected = useCallback((id: string) => {
    setSelectedRowIds(
      selectedRowIds.includes(id)
        ? selectedRowIds.filter((entry) => entry !== id)
        : [...selectedRowIds, id],
    );
  }, [selectedRowIds, setSelectedRowIds]);

  const goToPage = useCallback((next: number) => {
    if (options.pageIndex === undefined) setInternalPageIndex(next);
    options.onPageIndexChange?.(next);
  }, [options]);

  const modelRows = table.getRowModel().rows;

  // The page a reader is standing on can fall off the end without them touching
  // the pager: a refetch returns fewer rows, a record is deleted, a search
  // narrows the set. `autoResetPageIndex` is off — it also fires on every
  // background refetch, which would throw away the page they chose — so the
  // range is enforced here instead.
  //
  // Left uncorrected this is a dead end rather than a cosmetic slip: the row
  // model empties, `pageCount` falls to one, and `RecordTablePagination` stops
  // rendering at all, so the control that could walk them back is gone with it.
  const filteredRows = manual?.pagination ? modelRows : table.getFilteredRowModel().rows;
  const totalRowCount = manual?.pagination
    ? rowCount ?? rows.length
    : filteredRows.length;
  const pageCount = paginated ? Math.max(1, Math.ceil(totalRowCount / pageSize)) : 1;
  const safePageIndex = Math.min(Math.max(pageIndex, 0), pageCount - 1);
  const pageIndexOutOfRange = safePageIndex !== pageIndex;

  useEffect(() => {
    if (pageIndexOutOfRange) goToPage(safePageIndex);
  }, [goToPage, pageIndexOutOfRange, safePageIndex]);

  // The state correction above lands next render. Slicing the last valid page
  // here as well is what keeps the reader from seeing one frame of the empty
  // state on the way there.
  const renderedRows = useMemo(() => {
    const source = pageIndexOutOfRange && paginated && !manual?.pagination
      ? filteredRows.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize)
      : modelRows;
    return source.map((row) => row.original);
  }, [filteredRows, manual?.pagination, modelRows, pageIndexOutOfRange, pageSize, paginated, safePageIndex]);

  const activeSort = sorting[0];
  const visibleColumns = useMemo<RecordVisibleColumn<T>[]>(
    () => columns
      .map((column, index) => ({ column, id: columnIds[index] }))
      .filter(({ id }) => !columnVisibility || !hiddenColumns.includes(id))
      .map(({ column, id }) => ({
        id,
        column,
        sortable: sortable && !!column.sortValue,
        direction: activeSort?.id === id
          ? ((activeSort.desc ? "desc" : "asc") as RecordSortDirection)
          : null,
      })),
    [activeSort, columnIds, columnVisibility, columns, hiddenColumns, sortable],
  );

  // Counts come from the rows the *other* facets have already narrowed, not the
  // fully filtered set: a bucket has to keep showing what selecting it would
  // yield, or every unselected option in the facet you are standing in reads as
  // empty and the filter looks broken.
  const facetBuckets = useMemo<RecordFacetBucket<T>[]>(() => facetList.map((facet) => {
    const selected = facetSelection[facet.id] ?? [];
    const scope = manual?.facets
      ? rows
      : rows.filter((row) => facetList.every((other) => {
        if (other.id === facet.id) return true;
        const otherSelected = facetSelection[other.id] ?? [];
        if (otherSelected.length === 0) return true;
        const value = other.value(row);
        return value !== null && otherSelected.includes(value);
      }));

    const counts = new Map<string, number>();
    const order: string[] = [];
    for (const row of scope) {
      const value = facet.value(row);
      if (value === null) continue;
      if (!counts.has(value)) order.push(value);
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }

    const declared = facet.options;
    const options = declared
      ? declared.map((option) => ({
        ...option,
        // A declared bucket in manual mode gets NO derived count. The rows are
        // already narrowed by this very facet, so deriving one reports every
        // bucket the reader has not selected as empty — an instructor scoped to
        // one assignment would read "Essay one · 0" and conclude there is no
        // work waiting there. A page that knows the real counts passes them;
        // absent that, showing no number beats showing a false zero.
        count: option.count ?? (manual?.facets ? undefined : counts.get(option.value) ?? 0),
      }))
      : order.map((value) => ({ value, count: counts.get(value) ?? 0 }));

    return { facet, options, selected };
  }), [facetList, facetSelection, manual?.facets, rows]);

  const hideableColumns = useMemo(
    () => columns.map((column, index) => ({
      id: columnIds[index],
      name: columnName(column.header, column.name, `Column ${index + 1}`),
      hidden: hiddenColumns.includes(columnIds[index]),
    })),
    [columnIds, columns, hiddenColumns],
  );

  const pageRowIds = useMemo(() => renderedRows.map((row) => rowId(row)), [renderedRows, rowId]);
  const selectedOnPage = pageRowIds.filter((id) => selectedRowIds.includes(id));
  const pageSelectionState: "none" | "some" | "all" = selectedOnPage.length === 0
    ? "none"
    : selectedOnPage.length === pageRowIds.length ? "all" : "some";

  const togglePageSelected = useCallback(() => {
    const onPage = pageRowIds.filter((id) => selectedRowIds.includes(id));
    if (onPage.length === pageRowIds.length) {
      setSelectedRowIds(selectedRowIds.filter((id) => !pageRowIds.includes(id)));
      return;
    }
    const next = [...selectedRowIds];
    for (const id of pageRowIds) if (!next.includes(id)) next.push(id);
    if (!sameIds(next, selectedRowIds)) setSelectedRowIds(next);
  }, [pageRowIds, selectedRowIds, setSelectedRowIds]);

  const clearSelection = useCallback(() => setSelectedRowIds([]), [setSelectedRowIds]);

  const first = totalRowCount === 0 ? 0 : (paginated ? safePageIndex * pageSize : 0) + 1;
  const last = paginated
    ? Math.min(totalRowCount, (safePageIndex + 1) * pageSize)
    : totalRowCount;

  return {
    table,
    rows: renderedRows,
    rowId,
    primary,
    rowClassName: options.rowClassName,

    sortable,
    primarySortable: sortable && !!primary.sortValue,
    primaryDirection: activeSort?.id === PRIMARY_COLUMN_ID
      ? ((activeSort.desc ? "desc" : "asc") as RecordSortDirection)
      : null,
    visibleColumns,
    toggleSort,

    facetBuckets,
    toggleFacetValue,
    clearFacets,
    activeFacetCount: facetList.reduce(
      (total, facet) => total + (facetSelection[facet.id]?.length ?? 0),
      0,
    ),

    columnVisibility,
    hideableColumns,
    toggleColumnVisible,

    selectable,
    selectedRowIds,
    isRowSelected: (id: string) => selectedRowIds.includes(id),
    toggleRowSelected,
    togglePageSelected,
    pageSelectionState,
    clearSelection,

    paginated,
    pageIndex: safePageIndex,
    pageCount,
    pageSize,
    totalRowCount,
    pageBounds: { first, last },
    goToPage,
  };
}

export function facetColumnId(facetId: string): string {
  return `__facet_${facetId}`;
}

function toSortingState(sort: RecordSort | null): SortingState {
  return sort ? [{ id: sort.columnId, desc: sort.direction === "desc" }] : [];
}
